import React from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { CheckinReceiptPayment } from '@/utils/checkin-receipt-sheets'
import { TerminalCreditSalesSlip } from '@/components/molecules/front-tablet/receipt/terminal-credit-sales-slip'
import { QrCodeSalesSlip } from '@/components/molecules/front-tablet/receipt/qr-code-sales-slip'

type Props = {
  payment: CheckinReceiptPayment
}

type ProvisoMethod = NonNullable<CheckinReceiptPayment['receipt']>['provisoMethod']

const toProvisoMethodName = (provisoMethod: ProvisoMethod, t: TFunction): string => {
  switch (provisoMethod.type) {
    case 'brand':
      return provisoMethod.brandName
    case 'cash':
      return t('Cash')
    case 'creditCard':
      return t('Credit card')
  }
}

export const PaymentSection: React.FC<Props> = ({ payment: { receipt, usageAmount } }) => {
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  return (
    <>
      {receipt && (
        <>
          <div className="payment-card-block">
            <h2>{t('Receipt')}</h2>
            <div className="stats-block">
              <div>
                {t('SelfCheckIn.Issue')} : {dayjs().format('YYYY/MM/DD')}{' '}
              </div>
              <div>
                {t('SelfCheckIn.Reg. no.')} : {receipt.registrationNumber}
              </div>
            </div>
            <div className="detail-block">
              <div className="list-item-block">
                <div className="label">{t('address')}</div>
                <div>
                  {receipt.addressee} {t('SelfCheckIn.To')}
                </div>
              </div>
              <hr className="-normal" />
              <div className="list-item-block">
                <div className="label">{t('Amount of money')}</div>
                <div>¥{receipt.paymentAmount.toLocaleString()}</div>
              </div>
              <hr className="-normal" />
              <div className="breakdown-block">
                <div>
                  {t('SelfCheckIn.For 10%')}¥{receipt.totalAmountAt10Per.toLocaleString()}({t('Tax')}¥
                  {receipt.totalTaxAt10Per.toLocaleString()})
                </div>
                <div>
                  {t('SelfCheckIn.For 8%')}¥{receipt.totalAmountAt8Per.toLocaleString()}({t('Tax')}¥
                  {receipt.totalTaxAt8Per.toLocaleString()})
                </div>
              </div>
            </div>

            <div className="note-text">
              {t('SelfCheckIn.Received by {{n}} for accommodation.', { n: toProvisoMethodName(receipt.provisoMethod, t) })}
            </div>
            <div className="issuer-block">
              <div className="hotel-address">
                <div className="hotel-name">{pickByLanguage(receipt.hotel.name)}</div>
                <div>{pickByLanguage(receipt.hotel.address)}</div>
              </div>
              {receipt.taxOfficeName && (
                <div className="stamp-tax-declaration-box">
                  <div className="stamp-tax-declaration-body">
                    <p>印紙税申告納付につき{receipt.taxOfficeName}税務署承認済</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <hr className="-block" />
          <div className="sales-slip">
            {receipt.salesSlip?.type === 'credit' && <TerminalCreditSalesSlip receipt={receipt.salesSlip} />}
            {receipt.salesSlip?.type === 'qr' && <QrCodeSalesSlip receipt={receipt.salesSlip} />}
          </div>
        </>
      )}

      {usageAmount !== null && (
        <>
          <div className="payment-front-block">
            <h2>{t('SelfCheckIn.Details of use')}</h2>
            <div className="list-item-block">
              <div className="list-item">
                <span className="label">{t('SelfCheckinPayment.AmountUsed')}</span>
                <span className="value">¥{usageAmount.toLocaleString()}</span>
              </div>
              <div className="list-item">
                <span className="label">{t('SelfCheckinPayment.PaymentOption')}</span>
                <span className="value">{t('SelfCheckinPayment.PayAtTheFrontDesk')}</span>
              </div>
            </div>
          </div>
          <hr className="-block" />
        </>
      )}
    </>
  )
}
